'use client';

import { FingerprintJSPro } from '@fingerprintjs/fingerprintjs-pro-react';
import { useEffect } from 'react';
import { Env } from '@/consts/Env';
import { isProduction } from '@/libs/getEnvironment';
import { runWhenIdle } from '@/utils/runWhenIdle';

const subdomain = 'https://fingerprint.sweepking.com';
const scriptUrlPattern = `${subdomain}/web/v<version>/<apiKey>/loader_v<loaderVersion>.js`;

type FingerprintAgent = Awaited<ReturnType<typeof FingerprintJSPro.load>>;

let agentPromise: Promise<FingerprintAgent> | null = null;

const getApiKey = () => (isProduction()
  ? Env.NEXT_PUBLIC_FINGERPRINTJS_API_KEY_PROD
  : Env.NEXT_PUBLIC_FINGERPRINTJS_API_KEY_STAGING);

/**
 * Returns the Fingerprint agent, loading it on first call.
 * Login/sign-up/VPN checks call this directly, so they always get a request id
 * even if the idle warm-up below has not started yet.
 */
export function getFingerprintAgent(): Promise<FingerprintAgent> | null {
  if (agentPromise) {
    return agentPromise;
  }

  const apiKey = getApiKey();
  if (!apiKey || typeof window === 'undefined') {
    return null;
  }

  agentPromise = FingerprintJSPro.load({
    apiKey,
    endpoint: [subdomain, FingerprintJSPro.defaultEndpoint],
    scriptUrlPattern: [scriptUrlPattern, FingerprintJSPro.defaultScriptUrlPattern],
  }).catch((error: unknown) => {
    // Allow the next device check to retry instead of reusing a failed load.
    agentPromise = null;
    throw error;
  });

  return agentPromise;
}

export default function FingerprintAnalytics() {
  // The agent costs seconds of CPU on slow devices; warm it up once the page is idle
  // instead of on mount, so it does not delay the first render.
  useEffect(() => runWhenIdle(() => {
    getFingerprintAgent()?.catch(() => {
      // The device check that needs the agent will retry and surface the error.
    });
  }), []);

  return null;
}
